import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { usePageTitle } from "@/routes/__root";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AccountShell, Panel } from "@/components/site/AccountShell";
import { EmptyState, Loading, Notice, ProblemAlert } from "@/components/site/Feedback";
import { ManageOnWebsite, MembershipPlanCard } from "@/components/site/Membership";
import { api, errorMessage } from "@/lib/api";
import { isMobile } from "@/lib/config";
import { date, dateTime, minutesLabel, money } from "@/lib/format";
import {
  creditReasonLabel,
  creditTypeLabel,
  CreditType,
  paymentKindLabel,
  paymentStatusLabel,
  SubscriptionSource,
  SubscriptionStatus,
  subscriptionStatusLabel,
  type MemberSubscriptionDto,
} from "@/lib/types";
import { cn } from "@/lib/utils";

const routeApi = getRouteApi("/billing");

function StatusBadge({ active, label }: { active: boolean; label: string }) {
  return (
    <Badge
      variant="outline"
      className={cn(active ? "border-transparent bg-[image:var(--gradient-gold)] text-ink" : "bg-muted text-muted-foreground")}
    >
      {label}
    </Badge>
  );
}

/** Subscription state; used on the dashboard (compact) and the billing page. */
export function SubscriptionSummary({ subscription: s, compact }: { subscription: MemberSubscriptionDto; compact?: boolean }) {
  const cancelled = s.status === SubscriptionStatus.Canceled;
  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <StatusBadge
          active={s.hasActiveSubscription}
          label={s.hasActiveSubscription ? (cancelled ? "Active until period end" : "Active") : s.status ? subscriptionStatusLabel[s.status] : "No membership"}
        />
        {s.planName && <span className="font-medium">{s.planName}</span>}
      </div>
      {s.endDate && (
        <p className="text-muted-foreground">
          {s.hasActiveSubscription ? (cancelled || !s.canCancel ? "Access until " : "Renews / runs until ") : "Ended "}
          <span className="text-foreground">{date(s.endDate)}</span>
        </p>
      )}
      {!compact && s.startDate && <p className="text-muted-foreground">Member since {date(s.startDate)}</p>}
      {!compact && s.source && (
        <p className="text-muted-foreground">{s.source === SubscriptionSource.Stripe ? "Paid by card (Stripe)" : "Granted by Sir Levantine"}</p>
      )}
      {compact &&
        (isMobile ? (
          !s.hasActiveSubscription && <ManageOnWebsite className="mt-4" />
        ) : (
          <Button asChild variant={s.hasActiveSubscription ? "outlineGold" : "gold"} size="sm" className="mt-2">
            <Link to="/billing" search={{ checkout: undefined }}>
              {s.hasActiveSubscription ? "Manage billing" : "Subscribe"}
            </Link>
          </Button>
        ))}
    </div>
  );
}

export function BillingPage() {
  usePageTitle("Billing");
  if (isMobile) {
    return (
      <AccountShell title="Billing">
        <ManageOnWebsite>Manage your membership, payments and credit purchases on our website.</ManageOnWebsite>
      </AccountShell>
    );
  }
  return <WebBilling />;
}

function WebBilling() {
  const { checkout } = routeApi.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const subscription = useQuery({ queryKey: ["billing", "subscription"], queryFn: api.billing.subscription });

  useEffect(() => {
    if (checkout === "success") {
      // The Stripe webhook may land a moment after the redirect: refresh once more shortly after.
      void queryClient.invalidateQueries({ queryKey: ["billing"] });
      const t = setTimeout(() => void queryClient.invalidateQueries({ queryKey: ["billing"] }), 4000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [checkout, queryClient]);

  const cancel = useMutation({
    mutationFn: api.billing.cancelSubscription,
    onSuccess: (s) => {
      queryClient.setQueryData(["billing", "subscription"], s);
      void queryClient.invalidateQueries({ queryKey: ["billing"] });
      toast.success("Your membership was cancelled", {
        description: s.endDate ? `You keep access until ${date(s.endDate)}.` : undefined,
      });
    },
    onError: (e) => toast.error("The membership could not be cancelled", { description: errorMessage(e) }),
  });

  const dismiss = () => void navigate({ to: "/billing", search: { checkout: undefined }, replace: true });

  return (
    <AccountShell title="Billing" subtitle="Your membership, payments and credits.">
      {checkout === "success" && (
        <Notice tone="success" title="Payment received — thank you!" className="mb-8">
          Your purchase is being applied to your account; it can take a few seconds to appear.{" "}
          <button className="underline underline-offset-4" onClick={dismiss}>
            Dismiss
          </button>
        </Notice>
      )}
      {checkout === "cancelled" && (
        <Notice tone="warning" title="Checkout cancelled" className="mb-8">
          No payment was taken.{" "}
          <button className="underline underline-offset-4" onClick={dismiss}>
            Dismiss
          </button>
        </Notice>
      )}

      <div className="space-y-8">
        {subscription.isPending ? (
          <Loading />
        ) : subscription.isError ? (
          <ProblemAlert error={subscription.error} />
        ) : subscription.data.hasActiveSubscription ? (
          <Panel
            title="Membership"
            action={
              subscription.data.canCancel && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" disabled={cancel.isPending}>
                      {cancel.isPending && <Loader2 className="animate-spin" />}
                      Cancel membership
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-display text-2xl font-medium">Cancel your membership?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Your membership will not renew. You keep full access until{" "}
                        {subscription.data.endDate ? date(subscription.data.endDate) : "the end of the paid period"}. Credits you
                        bought stay on your account.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Keep membership</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={() => cancel.mutate()}
                      >
                        Cancel membership
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )
            }
          >
            <SubscriptionSummary subscription={subscription.data} />
          </Panel>
        ) : (
          <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
            <Panel title="Membership">
              <SubscriptionSummary subscription={subscription.data} />
              <p className="mt-4 text-sm text-muted-foreground">
                Subscribe to unlock the Academy video library. Renews monthly, cancel any time — see the{" "}
                <Link to="/legal/subscription" className="underline underline-offset-4">
                  Subscription Policy
                </Link>
                .
              </p>
            </Panel>
            <MembershipPlanCard title="Academy membership" />
          </div>
        )}

        <Tabs defaultValue="payments">
          <TabsList>
            <TabsTrigger value="payments">Payments</TabsTrigger>
            <TabsTrigger value="credits">Credit ledger</TabsTrigger>
            <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
          </TabsList>
          <TabsContent value="payments" className="mt-6">
            <Payments />
          </TabsContent>
          <TabsContent value="credits" className="mt-6">
            <CreditLedger />
          </TabsContent>
          <TabsContent value="subscriptions" className="mt-6">
            <SubscriptionHistory />
          </TabsContent>
        </Tabs>
      </div>
    </AccountShell>
  );
}

function TableCard({ children }: { children: React.ReactNode }) {
  return <div className="overflow-x-auto rounded-lg border border-border bg-card shadow-card">{children}</div>;
}

function Payments() {
  const q = useQuery({ queryKey: ["billing", "payments"], queryFn: api.billing.payments });
  if (q.isPending) return <Loading />;
  if (q.isError) return <ProblemAlert error={q.error} />;
  if (!q.data.items.length) return <EmptyState title="No payments yet" />;
  return (
    <TableCard>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Amount</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {q.data.items.map((p) => (
            <TableRow key={p.id}>
              <TableCell className="whitespace-nowrap">{dateTime(p.paidAt)}</TableCell>
              <TableCell>
                {p.description}
                {p.credits ? <span className="text-muted-foreground"> · {p.credits} credits</span> : null}
              </TableCell>
              <TableCell>{paymentKindLabel[p.kind]}</TableCell>
              <TableCell>{paymentStatusLabel[p.status]}</TableCell>
              <TableCell className="text-right font-medium">{money(p.amount, p.currency)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableCard>
  );
}

function CreditLedger() {
  const q = useQuery({ queryKey: ["billing", "credit-transactions"], queryFn: api.billing.creditTransactions });
  if (q.isPending) return <Loading />;
  if (q.isError) return <ProblemAlert error={q.error} />;
  if (!q.data.items.length) return <EmptyState title="No credit activity yet" />;
  return (
    <TableCard>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Credits</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead>Note</TableHead>
            <TableHead className="text-right">Change</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {q.data.items.map((t) => (
            <TableRow key={t.id}>
              <TableCell className="whitespace-nowrap">{dateTime(t.createdAt)}</TableCell>
              <TableCell>{creditTypeLabel[t.type]}</TableCell>
              <TableCell>{creditReasonLabel[t.reason]}</TableCell>
              <TableCell className="text-muted-foreground">{t.note ?? ""}</TableCell>
              <TableCell className={cn("text-right font-medium", t.amount < 0 ? "text-destructive" : "text-foreground")}>
                {t.type === CreditType.Mentorship
                  ? `${t.amount > 0 ? "+" : ""}${minutesLabel(t.amount)}`
                  : t.amount > 0
                    ? `+${t.amount}`
                    : t.amount}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableCard>
  );
}

function SubscriptionHistory() {
  const q = useQuery({ queryKey: ["billing", "subscriptions"], queryFn: api.billing.subscriptions });
  if (q.isPending) return <Loading />;
  if (q.isError) return <ProblemAlert error={q.error} />;
  if (!q.data.items.length) return <EmptyState title="No subscriptions yet" />;
  return (
    <TableCard>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Plan</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Start</TableHead>
            <TableHead>End</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {q.data.items.map((s) => (
            <TableRow key={s.id}>
              <TableCell>{s.planName ?? "Membership"}</TableCell>
              <TableCell>{s.isStripe ? "Stripe" : "Manual"}</TableCell>
              <TableCell>
                <StatusBadge active={s.isActive} label={subscriptionStatusLabel[s.status]} />
              </TableCell>
              <TableCell className="whitespace-nowrap">{date(s.startDate)}</TableCell>
              <TableCell className="whitespace-nowrap">{date(s.endDate)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableCard>
  );
}
